import React from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import PageHeader from "../../components/PageHeader";
import { useLocation, useParams } from "react-router-dom";
import { getAllBranches } from "../../services/BranchesService";
import {
  getAllSpecialities,
  createSpeciality,
  updateSpeciality,
  deleteSpeciality,
} from "../../services/SpecialitiesService";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const Specialities: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const venueId = params.branchId;

  console.log("Reports component - params:", params);
  console.log("Reports component - venueId from params:", venueId);

  const [branches, setBranches] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );

  const [search, setSearch] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);

  const formFields: Field[] = [{ name: "name", label: "Specialities" }];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);
  React.useEffect(() => {
    const fetchBranches = async () => {
      console.log("fetchBranches called with venueId:", venueId);
      try {
        if (!venueId) {
          console.error("No venue ID available, venueId:", venueId);
          setBranches([]);
          return;
        }

        console.log("Calling getAllBranches with venueId:", venueId);
        const response = await getAllBranches(venueId);
        console.log("Branches API response:", response);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        console.log("Processed branches data:", branchesData);
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping branch fetch");
    }
  }, [venueId]);

  React.useEffect(() => {
    const fetchSpecialities = async () => {
      setLoading(true);
      try {
        const businessId = selectValue ? String(selectValue) : venueId;
        const response = await getAllSpecialities(businessId);
        console.log("Specialities fetched:", response);

        let specialitiesData: any[] = [];
        if (response && Array.isArray(response)) {
          specialitiesData = response;
        } else if (response && response.data) {
          specialitiesData = Array.isArray(response.data)
            ? response.data
            : response.data.specialities || [];
        } else if (response && response.specialities) {
          specialitiesData = response.specialities;
        }

        const mapped = (specialitiesData || []).map((s: any) => ({
          id: s.id ?? s._id ?? s.speciality_id,
          name: s.specialities ?? s.name ?? s.speciality ?? "",
        }));

        setRows(mapped);
      } catch (error: any) {
        console.error("Error fetching specialities:", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialities();
  }, [refetchTrigger, selectValue, venueId]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader title="Specialities" />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          selectConfig={{
            options: branches.map((branch) => ({
              label: branch.name || "Unknown Branch",
              value: branch.business_id,
            })),
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select branch",
            size: "medium",
              label: "Select Branch",

            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[{ key: "name", header: "Specialities" }]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = rows.filter((r: any) => {
              if (!normalizedQuery) return true;

              const haystack = [r.name]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Speciality` : "Add Speciality"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? {
                  name: editingRow.name || "",
                }
              : {}
          }
          onSubmit={async (values) => {
            try {
              const businessId = selectValue ? String(selectValue) : venueId;

              if (editingRow) {
                await updateSpeciality(String(editingRow.id), {
                  name: values.name,
                  business_id: businessId,
                });
                toast.success("Speciality updated successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              } else {
                await createSpeciality({
                  name: values.name,
                  business_id: businessId,
                });
                toast.success("Speciality created successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              }
              setEditOpen(false);
              setEditingRow(null);
            } catch (error: any) {
              console.error("Error saving speciality:", error);
              showErrorToast(
                getErrorMessage(
                  error,
                  "Failed to save speciality. Please try again."
                )
              );
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
          try {
            await deleteSpeciality(String(toDeleteId));
            toast.success("Speciality deleted successfully!", {
              position: "top-right",
            });
            setRefetchTrigger((prev) => prev + 1);
          } catch (error: any) {
            console.error("Error deleting speciality:", error);
            showErrorToast(
              getErrorMessage(
                error,
                "Failed to delete speciality. Please try again."
              )
            );
          } finally {
            setConfirmOpen(false);
            setToDeleteId(null);
          }
        }}
        />
      </Box>
    </Box>
  );
};

export default Specialities;
